/**
 * Phosphor-CRT control primitives shared by the FANUC system screens
 * (SYSTEM / DGNOS / ADAPTER). These only add layout + interaction; all color
 * comes from the --fx-* vars the enclosing .fanuc-screen sets, so the config
 * pages read as the same control rather than a separate gray web form.
 */

/** A titled block with an optional right-aligned control/badge in the header. */
export function CrtSection({ title, right, children }) {
  return (
    <section className="mb-3">
      {(title || right) && (
        <div className="flex items-center justify-between gap-2 mb-1.5">
          {title ? (
            <h3 className="fanuc-label text-[10px] uppercase tracking-widest">{title}</h3>
          ) : (
            <span />
          )}
          {right}
        </div>
      )}
      {children}
    </section>
  );
}

/** A phosphor push-button. `active` fills it; `tone="warn"` for operator actions. */
export function CrtButton({ active, tone, disabled, onClick, title, className = '', children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={`crt-btn ${active ? 'is-on' : ''} ${tone === 'warn' ? 'crt-warn' : ''} px-3 py-1.5 text-xs font-bold tracking-wider ${className}`}
    >
      {children}
    </button>
  );
}

/** A labeled numeric field styled as a CRT input. */
export function CrtNumField({ label, value, onChange, onBlur, width = 'w-28' }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="fanuc-label text-[10px] uppercase tracking-wide">{label}</span>
      <input
        type="number"
        value={value}
        onChange={(e) => onChange(e.target.value === '' ? '' : Number(e.target.value))}
        onBlur={onBlur}
        className={`crt-input ${width} px-2 py-1 text-sm tabular-nums`}
      />
    </label>
  );
}

/** A label:value row, mono by default; `danger` flips the value to alarm red. */
export function CrtField({ label, value, mono = true, danger = false }) {
  return (
    <div className="flex items-center justify-between gap-2 text-sm">
      <span className="fanuc-label">{label}</span>
      <span
        className={`fanuc-value ${mono ? 'font-mono' : ''} tabular-nums`}
        style={danger ? { color: '#fca5a5', textShadow: '0 0 6px rgba(239,68,68,0.5)' } : undefined}
      >
        {value}
      </span>
    </div>
  );
}

/** A phosphor toggle switch. */
export function CrtToggle({ label, help, checked, disabled, onChange }) {
  return (
    <div className={`flex items-start gap-2 ${disabled ? 'opacity-60' : ''}`}>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        disabled={disabled}
        onClick={() => onChange(!checked)}
        className={`crt-switch mt-0.5 w-9 h-5 rounded-full relative shrink-0 ${checked ? 'is-on' : ''}`}
      >
        <span
          className={`absolute top-0.5 h-4 w-4 rounded-full ${checked ? 'left-4' : 'left-0.5'}`}
        />
      </button>
      <span className="flex flex-col">
        <span className="fanuc-value text-sm">{label}</span>
        {help && <span className="fanuc-label text-xs max-w-xs leading-snug">{help}</span>}
      </span>
    </div>
  );
}

/** Small pill badge, phosphor when `on`, struck-through dim when off. */
export function CrtBadge({ label, on }) {
  return (
    <span
      className={`px-2 py-0.5 rounded font-mono text-xs ${on ? 'fanuc-value' : 'fanuc-label line-through'}`}
      style={{ border: '1px solid color-mix(in srgb, var(--fx-dim) 45%, transparent)' }}
    >
      {label}
    </span>
  );
}

/** An inset notice line (info / warn / bad). */
export function CrtNotice({ tone = 'info', children }) {
  const style =
    tone === 'bad'
      ? { color: '#fecaca', background: 'rgba(127,29,29,0.4)', borderColor: '#ef4444' }
      : tone === 'warn'
      ? { color: '#fde68a', background: 'rgba(120,80,0,0.3)', borderColor: 'color-mix(in srgb, #f59e0b 60%, transparent)' }
      : { color: 'var(--fx-dim)', background: 'rgba(0,0,0,0.3)', borderColor: 'color-mix(in srgb, var(--fx-dim) 45%, transparent)' };
  return (
    <div className="rounded border px-3 py-2 text-sm leading-snug" style={{ ...style, borderWidth: 1 }}>
      {children}
    </div>
  );
}
